import { Link } from "react-router-dom";
import { Instagram, Send } from "lucide-react";
import { useState } from "react";
import { api, imageUrl, formatApiError } from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import { Reveal } from "@/components/site/Reveal";
import { MonogramK } from "@/components/site/Logo";
import { toast } from "sonner";

export default function Contact() {
  const { settings } = useShop();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const igUrl = settings?.instagram_url || "https://www.instagram.com/kubwalofragrances/";
  const igHandle = settings?.instagram_handle || "@kubwalofragrances";

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/contact", form);
      setSent(true);
      if (data.status === "sent") {
        toast.success("Message sent — we'll be in touch soon.");
      } else {
        toast.success("Message received. We'll reply as soon as possible.");
      }
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSending(false);
  };

  const field = (key, label, opts = {}) => (
    <div key={key}>
      <label className="font-sans-body text-[10px] uppercase tracking-[0.25em] text-[#C6A15B] block mb-2">{label}</label>
      <input required {...opts} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="w-full bg-transparent border border-[#D4AF37]/25 px-4 py-3.5 font-sans-body text-sm text-white placeholder:text-[#666666] focus:outline-none focus:border-[#D4AF37] transition-colors"
        data-testid={`contact-${key}-input`} />
    </div>
  );

  return (
    <div className="pt-20" data-testid="contact-page">
      <div className="bg-[#0A0A0A] gold-hairline-b py-16 sm:py-24 relative grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 text-center">
          <Reveal>
            <p className="eyebrow mb-4">We're Listening</p>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white font-light" data-testid="contact-title">
              Contact Kubwalo Fragrances
            </h1>
          </Reveal>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
          {/* Contact form */}
          <Reveal>
            <div className="gold-hairline bg-[#0D0D0D] p-8 sm:p-10">
              <h2 className="font-serif-display text-3xl text-white font-light">Send a Message</h2>
              <p className="font-sans-body text-sm text-[#B8B8B8] font-light mt-3">
                Questions about an order, a fragrance, or anything else — we'd love to hear from you.
              </p>
              {sent ? (
                <div className="mt-10 gold-hairline bg-[#080808] p-10 text-center" data-testid="contact-success">
                  <Send size={28} className="text-[#D4AF37] mx-auto" strokeWidth={1.2} />
                  <p className="font-serif-display text-2xl text-white mt-5">Message received.</p>
                  <p className="font-sans-body text-sm text-[#B8B8B8] font-light mt-2">
                    Thank you for reaching out — we'll reply to your email shortly.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-8 space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {field("name", "Your Name")}
                    {field("email", "Email Address", { type: "email" })}
                  </div>
                  {field("subject", "Subject")}
                  <div>
                    <label className="font-sans-body text-[10px] uppercase tracking-[0.25em] text-[#C6A15B] block mb-2">Message</label>
                    <textarea required minLength={5} rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="w-full bg-transparent border border-[#D4AF37]/25 px-4 py-3.5 font-sans-body text-sm text-white placeholder:text-[#666666] focus:outline-none focus:border-[#D4AF37] resize-none transition-colors"
                      data-testid="contact-message-input" />
                  </div>
                  <button type="submit" className="btn-gold w-full" disabled={sending} data-testid="contact-submit-button">
                    {sending ? "Sending..." : "Submit Message"}
                  </button>
                </form>
              )}
              <div className="mt-8 gold-hairline-t pt-6 font-sans-body text-sm text-[#B8B8B8] font-light space-y-2">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#C6A15B]">Kubwalo Fragrances</p>
                {settings?.contact_email && (
                  <p>Email: <a href={`mailto:${settings.contact_email}`} className="text-[#D4AF37] hover:underline underline-offset-4" data-testid="contact-email-link">{settings.contact_email}</a></p>
                )}
                <p>
                  Instagram:{" "}
                  <a href={igUrl} target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline underline-offset-4" data-testid="contact-instagram-handle">
                    {igHandle}
                  </a>
                </p>
              </div>
            </div>
          </Reveal>

          {/* Instagram QR */}
          <Reveal delay={0.15}>
            <div className="gold-hairline bg-[#0D0D0D] p-8 sm:p-10 text-center relative grain" data-testid="instagram-section">
              <p className="eyebrow mb-3">Stay Connected</p>
              <h2 className="font-serif-display text-3xl sm:text-4xl text-white font-light">Follow the Fragrance.</h2>
              <p className="font-sans-body text-sm text-[#B8B8B8] font-light mt-4 max-w-sm mx-auto leading-relaxed">
                Discover our latest releases, explore the collection, and stay connected
                with Kubwalo Fragrances on Instagram.
              </p>

              <div className="mt-10 mx-auto w-64 h-64 sm:w-72 sm:h-72 gold-hairline bg-[#080808] relative flex items-center justify-center p-4" data-testid="instagram-qr-frame">
                <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 45%, rgba(212,175,55,0.12), transparent 65%)" }} />
                {settings?.instagram_qr_url ? (
                  <img src={imageUrl(settings.instagram_qr_url)} alt="Kubwalo Fragrances Instagram QR code"
                    className="relative w-full h-full object-contain" data-testid="instagram-qr-image" />
                ) : (
                  <div className="relative flex flex-col items-center gap-4" data-testid="instagram-qr-placeholder">
                    <MonogramK size={64} />
                    <p className="font-sans-body text-[10px] uppercase tracking-[0.25em] text-[#C6A15B] max-w-[200px] leading-relaxed">
                      Our Instagram QR code will appear here soon
                    </p>
                  </div>
                )}
              </div>

              <a href={igUrl} target="_blank" rel="noopener noreferrer"
                className="mt-6 inline-block font-sans-body text-sm text-[#D4AF37] hover:underline underline-offset-4"
                data-testid="instagram-handle-link">
                {igHandle}
              </a>
              <div className="mt-6">
                <a href={igUrl} target="_blank" rel="noopener noreferrer" className="btn-gold" data-testid="instagram-follow-button">
                  <Instagram size={15} /> Follow Us on Instagram
                </a>
              </div>
            </div>
          </Reveal>
        </div>

        <p className="text-center mt-16 font-sans-body text-xs text-[#666666] font-light" data-testid="contact-order-help">
          Questions about an existing order?{" "}
          <Link to="/contact" className="text-[#D4AF37] underline underline-offset-4">Message us</Link> with your order number
          and we'll take care of it.
        </p>
      </div>
    </div>
  );
}
